/*
 * Qeydo Quick View v29
 * A dedicated, isolated product preview for standard product cards.
 * Door Compatibility uses its own dialog and is intentionally not handled here.
 * Variant choices reuse the product page markup, while selection and purchase
 * state remain local to the preview.
 */
if (!customElements.get("qeydo-quick-view")) {
  customElements.define(
    "qeydo-quick-view",
    class QeydoQuickView extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.loadingController = null;
        this.resizeTimer = null;
        this.currentVariant = null;
        this.initialVariantId = null;
        this.initialInventoryHTML = "";
        this.availableAddButtonHTML = "";
        this.mobileStickyObserver = null;
        this.mobileStickyMutationObserver = null;

        this.onModalClick = this.onModalClick.bind(this);
        this.onModalChange = this.onModalChange.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        super.connectedCallback?.();
        this.modalContent?.addEventListener("click", this.onModalClick);
        this.modalContent?.addEventListener("change", this.onModalChange);
        window.addEventListener("resize", this.onResize, { passive: true });
      }

      disconnectedCallback() {
        this.modalContent?.removeEventListener("click", this.onModalClick);
        this.modalContent?.removeEventListener("change", this.onModalChange);
        window.removeEventListener("resize", this.onResize);
        this.loadingController?.abort();
        this.destroyMobileSticky();
        this.setQuickViewPageState(false);
        clearTimeout(this.resizeTimer);
      }

      async show(opener) {
        if (!opener || opener.getAttribute("aria-disabled") === "true") return;

        this.setOpenerLoading(opener, true);
        this.loadingController?.abort();
        this.loadingController = new AbortController();

        try {
          const response = await fetch(opener.getAttribute("data-product-url"), {
            signal: this.loadingController.signal,
          });
          if (!response.ok) throw new Error(`Quick View HTTP ${response.status}`);

          const documentHTML = new DOMParser().parseFromString(
            await response.text(),
            "text/html"
          );
          const productSection = documentHTML.querySelector(
            'section[id^="MainProduct-"]'
          );
          if (!productSection) throw new Error("Quick View product section missing");

          this.prepareProductSection(productSection, opener);
          this.modalContent.replaceChildren(
            ...Array.from(productSection.childNodes).map((node) => node.cloneNode(true))
          );

          this.finishInsertedContent(opener);
          this.handleVariantSelection(
            this.currentVariant,
            this.modalContent.querySelector("[data-qeydo-qv-picker]"),
            {
            initial: true,
            }
          );
          super.show(opener);
          this.setQuickViewPageState(true);
          this.resetScrollPosition();
          this.initializeGallery();
          this.initializeMobileSticky();
          this.resetScrollAfterLayout();
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Qeydo Quick View:", error);
          }
        } finally {
          this.setOpenerLoading(opener, false);
        }
      }

      hide(preventFocus = false) {
        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer) cartDrawer.setActiveElement(this.openedBy);

        if (preventFocus) this.openedBy = null;
        super.hide();

        this.destroyGallery();
        this.destroyMobileSticky();
        this.setQuickViewPageState(false);
        this.loadingController?.abort();
        this.resetAllScrollPositions();
        this.modalContent.replaceChildren();
        this.classList.remove("qeydo-quick-view--sold-out");
        this.currentVariant = null;
        this.initialVariantId = null;
        this.initialInventoryHTML = "";
        this.availableAddButtonHTML = "";
      }

      setOpenerLoading(opener, loading) {
        opener.toggleAttribute("aria-disabled", loading);
        opener.classList.toggle("loading", loading);
        opener
          .querySelector(".loading-overlay__spinner")
          ?.classList.toggle("hidden", !loading);
      }

      prepareProductSection(productSection, opener) {
        const sectionId = productSection.dataset.section;
        const uniqueSectionId = `quickadd-${sectionId}`;
        const productUrl = opener.getAttribute("data-product-url");
        const doorCompatibilityId = productSection.querySelector(
          ".door-compatibility--product-block"
        )?.id;

        // The preview never executes copied page scripts. Registered Shopify
        // custom elements upgrade normally after insertion and retain their
        // JSON data scripts, without duplicating page-level listeners.
        productSection
          .querySelectorAll("script:not([data-selected-variant]):not([data-all-variants-no-high])")
          .forEach((script) => script.remove());

        if (sectionId) {
          productSection.innerHTML = productSection.innerHTML.replaceAll(
            sectionId,
            uniqueSectionId
          );
          productSection
            .querySelectorAll("variant-selects, variant-radios")
            .forEach((picker) => {
              picker.dataset.originalSection = sectionId;
              picker.dataset.updateUrl = "false";
            });
        }

        this.currentVariant = this.captureVariantAndPreparePicker(productSection);
        this.prepareQuickViewInformation(
          productSection,
          productUrl,
          doorCompatibilityId,
          opener
        );
        this.removeNonEssentialBlocks(productSection);
        this.addDetailsLink(productSection, productUrl);
      }

      prepareQuickViewInformation(
        productSection,
        productUrl,
        doorCompatibilityId,
        opener
      ) {
        const sourceCard = opener.closest(".card-wrapper") || opener.closest(".card");
        const info = productSection.querySelector(".product__info-container");
        const price = info?.querySelector(".price-wrapper, .price");
        const originalDoors = info?.querySelector(".compatible-doors-block");
        const originalDoorLink = originalDoors?.querySelector(
          ".compatible-doors-card__link"
        );

        // The standard product card already exposes the exact, product-specific
        // compact data families. Reusing those rendered nodes keeps Quick View
        // aligned with the same metafields without introducing parallel data.
        const accessFamily = sourceCard
          ?.querySelector(".card-information-family--access")
          ?.cloneNode(true);
        const essentialFamily = sourceCard
          ?.querySelector(".card-information-family--essential")
          ?.cloneNode(true);
        const compatibilityFamily = sourceCard
          ?.querySelector(".card-information-family--compatibility")
          ?.cloneNode(true);

        [accessFamily, essentialFamily, compatibilityFamily]
          .filter(Boolean)
          .forEach((family) => family.classList.add("qeydo-qv-family"));

        if (compatibilityFamily && productUrl) {
          const link = originalDoorLink?.cloneNode(true) || document.createElement("a");
          const destination = new URL(productUrl, window.location.origin);
          if (doorCompatibilityId) destination.hash = doorCompatibilityId;
          link.href = `${destination.pathname}${destination.search}${destination.hash}`;
          link.removeAttribute("data-open-compatibility");
          link.className = "qeydo-qv-compatibility__link focus-inset";
          if (!originalDoorLink) {
            link.innerHTML = '<span>Vérifier ma porte</span><span aria-hidden="true">→</span>';
          }
          compatibilityFamily.appendChild(link);
        }

        const reassurance = document.createElement("div");
        reassurance.className = "qeydo-qv-reassurance";
        const cardBenefits = sourceCard?.querySelector(
          ".card-information__benefits"
        );
        const pageBenefits = info?.querySelector(".product-tags");
        const benefits = (cardBenefits || pageBenefits)?.cloneNode(true);
        if (benefits) {
          benefits.classList.add("qeydo-qv-reassurance__benefits");
          reassurance.appendChild(benefits);
        }

        let inventory = info?.querySelector(".product__inventory");
        if (inventory && this.currentVariant) {
          inventory.innerHTML = this.currentVariant.available
            ? '<span class="product__inventory-inner bold qeydo-quick-view__inventory-status qeydo-quick-view__inventory-status--available">En stock</span>'
            : '<span class="product__inventory-inner product__inventory-inner--empty bold qeydo-quick-view__inventory-status">En rupture de stock</span>';
        }
        if (!inventory && this.currentVariant) {
          inventory = document.createElement("div");
          inventory.className = "product__inventory";
          inventory.innerHTML = this.currentVariant.available
            ? '<span class="product__inventory-inner bold qeydo-quick-view__inventory-status qeydo-quick-view__inventory-status--available">En stock</span>'
            : '<span class="product__inventory-inner product__inventory-inner--empty bold qeydo-quick-view__inventory-status">En rupture de stock</span>';
        }
        if (inventory) reassurance.appendChild(inventory);

        pageBenefits?.remove();
        const variantBlock = info?.querySelector(
          ".product-parameters.qeydo-qv-product-parameters"
        );
        const reassuranceAnchor = variantBlock || price;
        if (reassurance.children.length && reassuranceAnchor) {
          reassuranceAnchor.after(reassurance);
        }

        let insertionPoint = reassurance.children.length
          ? reassurance
          : reassuranceAnchor;
        [accessFamily, essentialFamily, compatibilityFamily]
          .filter(Boolean)
          .forEach((family) => {
            insertionPoint?.after(family);
            insertionPoint = family;
          });

        // Remove the non-decision label only inside the fetched preview.
        info?.querySelectorAll(".product__text").forEach((label) => {
          if (label.textContent.trim().toLocaleUpperCase("fr") === "SMART LOCK") {
            label.remove();
          }
        });

        const powerBlock = productSection.querySelector('[id^="ProductPower-"]');
        powerBlock?.remove();
        productSection
          .querySelector(".essential-information-card")
          ?.closest(".product-detail-card-block")
          ?.remove();
        productSection.querySelectorAll(".product__description").forEach(
          (element) => element.remove()
        );
        productSection.querySelectorAll(".complementary-products-block").forEach(
          (element) => element.remove()
        );
      }

      captureVariantAndPreparePicker(productSection) {
        const picker = productSection.querySelector(
          "variant-selects, variant-radios"
        );
        let variant = this.readSelectedVariant(picker);

        // Products with only one variant do not render a picker. Keep their
        // existing form variant and availability without changing the price.
        if (!variant) {
          const variantInput = productSection.querySelector(
            'form[action*="/cart/add"] input[name="id"]'
          );
          const addButton = productSection.querySelector(
            ".product-form__submit"
          );
          const variantId = Number(variantInput?.value);
          if (variantId) {
            variant = {
              id: variantId,
              available: !addButton?.disabled,
            };
          }
        }

        // Keep the native controls and JSON, but replace the page custom element
        // with a local wrapper so the Quick View never fetches/replaces product
        // fragments. Selection is handled by this modal only.
        if (picker) {
          const pickerBlock = picker.closest(".product-parameters") || picker;
          const localPicker = document.createElement("div");
          localPicker.className = `${picker.className} qeydo-qv-variant-picker`;
          localPicker.dataset.qeydoQvPicker = "";
          localPicker.innerHTML = picker.innerHTML;
          picker.replaceWith(localPicker);
          pickerBlock.classList.add("qeydo-qv-product-parameters");
        }

        return variant;
      }

      removeNonEssentialBlocks(productSection) {
        const selectors = [
          ".product-popup",
          ".about",
          ".share-buttons",
          ".product__sku",
          ".linked-products",
          ".breadcrumb",
          ".pickup-availability",
          ".product__text-icons",
          ".product-recommendations",
          ".technical-specifications-spacing",
          ".door-compatibility--product-block",
          ".qeydo-reviews-block--summary",
          ".access-modes-block",
          ".airbnb-compatibility-block",
          ".compatible-doors-block",
        ];
        productSection
          .querySelectorAll(selectors.join(","))
          .forEach((element) => element.remove());

        productSection
          .querySelectorAll(".direct-contact-card-block")
          .forEach((element) => {
            if (
              element.querySelector('a[href*="wa.me"]') ||
              element.querySelector(".icon-qeydo-whatsapp")
            ) {
              element.remove();
            }
          });

        productSection
          .querySelectorAll(".direct-contact-card-block")
          .forEach((element) => element.remove());

        productSection.querySelectorAll(".custom-liquid").forEach((element) => {
          if (element.querySelector(".qeydo-service-note")) element.remove();
        });
      }

      addDetailsLink(productSection, productUrl) {
        const formButtons = productSection.querySelector(".product-form__buttons");
        if (!formButtons || formButtons.querySelector(".product__full-details")) return;

        const link = document.createElement("a");
        link.href = productUrl;
        link.className = "product__full-details button button--simple button--arrow";
        link.innerHTML = '<span>Afficher tous les détails</span><span aria-hidden="true">→</span>';
        formButtons.appendChild(link);
      }

      finishInsertedContent(opener) {
        this.prepareQuickViewReviews();

        const compactReviewLink = this.modalContent.querySelector(
          ".qeydo-reviews-block--compact [data-qeydo-reviews-anchor]"
        );
        if (compactReviewLink) compactReviewLink.href = opener.getAttribute("data-product-url");

        this.updateImageSizes();
        this.installSafeBuyNowButton();

        const inventory = this.modalContent.querySelector(".product__inventory");
        this.initialInventoryHTML = inventory?.innerHTML || "";

        const addButton = this.modalContent.querySelector(".product-form__submit");
        if (addButton && !addButton.disabled) {
          this.availableAddButtonHTML = addButton.innerHTML;
        } else {
          this.availableAddButtonHTML = `<span>${
            window.variantStrings?.addToCart || "Ajouter au panier"
          }</span>`;
        }

        if (window.ProductModel) window.ProductModel.loadShopifyXR();
      }

      setQuickViewPageState(open) {
        document.body.classList.toggle("qeydo-quick-view-open", open);
      }

      initializeMobileSticky() {
        this.destroyMobileSticky();
        const content = this.querySelector(".quick-add-modal__content");
        const scroller = this.getScrollContainer();
        const sourceButton = this.modalContent.querySelector(
          ".product-form__submit"
        );
        if (!content || !scroller || !sourceButton) return;

        const footer = document.createElement("div");
        footer.className = "qeydo-qv-mobile-cart";
        footer.setAttribute("aria-hidden", "true");
        const button = document.createElement("button");
        button.type = "button";
        button.className = `${sourceButton.className} qeydo-qv-mobile-cart__button`;
        footer.appendChild(button);
        content.appendChild(footer);

        const sync = () => {
          button.innerHTML = sourceButton.innerHTML;
          button.disabled = sourceButton.disabled;
          button.setAttribute(
            "aria-disabled",
            sourceButton.disabled ? "true" : "false"
          );
        };
        sync();
        button.addEventListener("click", () => {
          if (!button.disabled) sourceButton.click();
        });

        this.mobileStickyMutationObserver = new MutationObserver(sync);
        this.mobileStickyMutationObserver.observe(sourceButton, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        this.mobileStickyObserver = new IntersectionObserver(
          ([entry]) => {
            const visible =
              window.matchMedia("(max-width: 749px)").matches &&
              !entry.isIntersecting;
            footer.classList.toggle("is-visible", visible);
            footer.setAttribute("aria-hidden", visible ? "false" : "true");
          },
          { root: scroller, threshold: 0.2 }
        );
        this.mobileStickyObserver.observe(sourceButton);
      }

      destroyMobileSticky() {
        this.mobileStickyObserver?.disconnect();
        this.mobileStickyMutationObserver?.disconnect();
        this.mobileStickyObserver = null;
        this.mobileStickyMutationObserver = null;
        this.querySelector(".qeydo-qv-mobile-cart")?.remove();
      }

      prepareQuickViewReviews() {
        const block = this.modalContent.querySelector(
          ".qeydo-reviews-block--compact"
        );
        const link = block?.querySelector("[data-qeydo-reviews-anchor]");
        const stars = link?.querySelector(".qeydo-reviews__stars");
        const rating = link?.querySelector(".qeydo-reviews-compact__rating");
        const count = link?.querySelector(".qeydo-reviews-compact__count");
        if (!block || !link || !stars || !rating || !count) return;

        block.querySelector(".qeydo-reviews-info")?.remove();
        block.classList.add("qeydo-reviews-block--card");
        link.classList.remove("qeydo-reviews-compact");
        link.classList.add("qeydo-card-reviews");
        rating.classList.replace(
          "qeydo-reviews-compact__rating",
          "qeydo-card-reviews__rating"
        );
        count.classList.replace(
          "qeydo-reviews-compact__count",
          "qeydo-card-reviews__count"
        );
        count.textContent = `(${count.textContent.trim()})`;
        link.replaceChildren(stars, rating, count);
      }

      installSafeBuyNowButton() {
        const checkout = this.modalContent.querySelector(".product-form__checkout");
        if (!checkout) return;

        // Never initialize Shopify.PaymentButton in Quick View.  Its injected
        // iframe/subtree is the source of the destructive sold-out reflow.
        checkout.classList.remove("no-js-hidden");
        checkout.replaceChildren();

        const button = document.createElement("button");
        button.type = "button";
        button.className =
          "button button--primary qeydo-quick-view__buy-now focus-inset";
        button.textContent = "Acheter maintenant";
        checkout.appendChild(button);
      }

      updateImageSizes() {
        const product = this.modalContent.querySelector(".product");
        if (!product?.classList.contains("product--columns")) return;

        let sizes =
          "(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)";
        if (product.classList.contains("product--medium")) sizes = sizes.replace("715px", "605px");
        if (product.classList.contains("product--small")) sizes = sizes.replace("715px", "495px");
        product.querySelectorAll(".product__media img").forEach((image) => {
          image.setAttribute("sizes", sizes);
        });
      }

      readSelectedVariant(picker) {
        const data = picker
          ?.querySelector("[data-selected-variant]")
          ?.textContent?.trim();
        if (!data) return null;
        try {
          return JSON.parse(data);
        } catch (_) {
          return null;
        }
      }

      readVariants(picker) {
        const data = picker
          ?.querySelector("[data-all-variants-no-high]")
          ?.textContent?.trim();
        if (!data) return [];
        try {
          return JSON.parse(data);
        } catch (_) {
          return [];
        }
      }

      getVariantFromPicker(picker) {
        const values = Array.from(
          picker?.querySelectorAll(".product-form__controls") || []
        ).map((control) => {
          const checked = control.querySelector('input[type="radio"]:checked');
          const select = control.querySelector("select");
          return checked?.value ?? select?.value ?? null;
        });
        if (!values.length || values.some((value) => value == null)) return null;

        return (
          this.readVariants(picker).find((variant) =>
            variant.options?.every((option, index) => option === values[index])
          ) || null
        );
      }

      updatePickerLabels(picker) {
        picker?.querySelectorAll(".product-form__controls").forEach((control) => {
          const checked = control.querySelector('input[type="radio"]:checked');
          const select = control.querySelector("select");
          const labelValue = control.querySelector('[id^="ColorName-"]');
          if (labelValue) labelValue.textContent = checked?.value ?? select?.value ?? "";
        });
      }

      handleVariantSelection(variant, picker, { initial = false } = {}) {
        this.currentVariant = variant || null;
        if (initial) this.initialVariantId = variant?.id || null;

        const selectedVariantScript = picker?.querySelector(
          "[data-selected-variant]"
        );
        if (selectedVariantScript) {
          selectedVariantScript.textContent = variant
            ? JSON.stringify(variant)
            : "";
        }

        this.updateFormVariantIds(variant);
        this.updatePrice(variant);
        this.updateInventory(variant);
        this.syncPurchaseState(variant);

        if (variant?.featured_media) this.updateVariantMedia(variant);

        this.modalContent
          .querySelectorAll(".js-media-list, .js-media-sublist")
          .forEach((element) => element.swiper?.update());
      }

      updateVariantMedia(variant) {
        const mediaId = String(variant.featured_media?.id || "");
        if (!mediaId) return;
        const media = Array.from(
          this.modalContent.querySelectorAll("[data-media-id]")
        ).find((item) => String(item.dataset.mediaId || "").endsWith(`-${mediaId}`));
        if (!media) return;

        const slide = media.closest(".swiper-slide");
        const list = media.closest(".js-media-list");
        if (slide && list?.swiper) {
          const index = Array.from(slide.parentElement.children).indexOf(slide);
          if (index >= 0) list.swiper.slideTo(index);
        }
      }

      updateFormVariantIds(variant) {
        this.modalContent
          .querySelectorAll('form[action*="/cart/add"] input[name="id"]')
          .forEach((input) => {
            input.value = variant?.id || "";
            input.disabled = !variant;
          });
      }

      formatVariantMoney(cents) {
        if (typeof formatMoney === "function") return formatMoney(cents);
        return new Intl.NumberFormat(document.documentElement.lang || "fr-FR", {
          style: "currency",
          currency: "MAD",
        }).format((Number(cents) || 0) / 100);
      }

      updatePrice(variant) {
        const price = this.modalContent.querySelector(".price");
        if (!price || !variant || variant.price == null) return;

        const onSale =
          Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        price.classList.toggle("price--on-sale", onSale);
        price.classList.toggle("price--sold-out", variant.available === false);

        const regular = price.querySelector(
          ".price__regular .price-item--regular"
        );
        const sale = price.querySelector(".price__sale .price-item--sale");
        const compare = price.querySelector(
          ".price__sale .price__compare .price-item--regular"
        );
        const currentPrice = this.formatVariantMoney(variant.price);

        if (regular) regular.textContent = currentPrice;
        if (sale) sale.textContent = currentPrice;
        if (compare) {
          compare.textContent = onSale
            ? this.formatVariantMoney(variant.compare_at_price)
            : "";
        }
      }

      updateInventory(variant) {
        const inventory = this.modalContent.querySelector(".product__inventory");
        if (!inventory) return;

        if (variant?.available === false || !variant) {
          inventory.innerHTML =
            '<span class="product__inventory-inner product__inventory-inner--empty bold qeydo-quick-view__inventory-status">En rupture de stock</span>';
          return;
        }

        if (
          this.initialInventoryHTML &&
          String(variant.id) === String(this.initialVariantId)
        ) {
          inventory.innerHTML = this.initialInventoryHTML;
        } else {
          inventory.innerHTML =
            '<span class="product__inventory-inner bold qeydo-quick-view__inventory-status qeydo-quick-view__inventory-status--available">En stock</span>';
        }
      }

      syncPurchaseState(variant = this.currentVariant) {
        const addButton = this.modalContent.querySelector(".product-form__submit");
        if (!addButton) return;

        const soldOut = !variant || variant.available === false;

        this.classList.toggle("qeydo-quick-view--sold-out", soldOut);
        addButton.disabled = soldOut;
        addButton.setAttribute("aria-disabled", soldOut ? "true" : "false");

        if (soldOut) {
          addButton.innerHTML = `<span>${
            window.variantStrings?.soldOut || "Épuisé"
          }</span>`;
        } else if (this.availableAddButtonHTML) {
          addButton.innerHTML = this.availableAddButtonHTML;
        }

        const buyNow = this.modalContent.querySelector(
          ".qeydo-quick-view__buy-now"
        );
        if (buyNow) {
          buyNow.disabled = soldOut;
          buyNow.classList.toggle("qeydo-disabled", soldOut);
          buyNow.setAttribute("aria-disabled", soldOut ? "true" : "false");
        }

        const quantityInput = this.modalContent.querySelector("quantity-input");
        quantityInput?.querySelectorAll("button, input").forEach((control) => {
          control.disabled = soldOut;
        });
        if (!soldOut) quantityInput?.updateButtonStates?.();
      }

      onModalClick(event) {
        const buyNow = event.target.closest(".qeydo-quick-view__buy-now");
        if (!buyNow || buyNow.disabled) return;
        event.preventDefault();
        this.buyNow(buyNow);
      }

      onModalChange(event) {
        const picker = event.target.closest("[data-qeydo-qv-picker]");
        if (!picker) return;
        this.updatePickerLabels(picker);
        this.handleVariantSelection(this.getVariantFromPicker(picker), picker);
      }

      async buyNow(button) {
        const variantId = this.currentVariant?.id;
        if (!variantId || this.currentVariant?.available === false) return;

        const quantity = Math.max(
          1,
          Number(this.modalContent.querySelector(".quantity__input")?.value) || 1
        );
        const root = window.Shopify?.routes?.root || "/";
        const originalText = button.textContent;

        button.disabled = true;
        button.classList.add("loading");
        button.setAttribute("aria-busy", "true");

        try {
          const response = await fetch(`${root}cart/add.js`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              items: [{ id: Number(variantId), quantity }],
            }),
          });
          if (!response.ok) throw new Error(`Buy now HTTP ${response.status}`);
          window.location.assign(`${root}checkout`);
        } catch (error) {
          console.error("Qeydo Quick View buy now:", error);
          button.textContent = originalText;
          button.disabled = false;
          button.classList.remove("loading");
          button.removeAttribute("aria-busy");
        }
      }

      getScrollContainer() {
        return this.querySelector(".quick-add-modal__content-wrapper");
      }

      resetScrollPosition() {
        const scroller = this.getScrollContainer();
        if (scroller) {
          scroller.scrollTop = 0;
          scroller.scrollLeft = 0;
        }
      }

      resetAllScrollPositions() {
        const containers = [
          this.getScrollContainer(),
          this.modalContent,
          this.modalContent?.querySelector(".quick-add-modal__content-info"),
          this.modalContent?.querySelector(".product"),
          this.modalContent?.querySelector(".product__outer"),
          this.modalContent?.querySelector(".product__info-container"),
        ];

        containers.filter(Boolean).forEach((container) => {
          container.scrollTop = 0;
          container.scrollLeft = 0;
        });
      }

      resetScrollAfterLayout() {
        requestAnimationFrame(() => {
          this.resetAllScrollPositions();
          requestAnimationFrame(() => this.resetAllScrollPositions());
        });
      }

      destroyGallery() {
        this.modalContent
          ?.querySelectorAll(".js-media-list, .js-media-sublist")
          .forEach((element) => element.swiper?.destroy(true, true));
      }

      initializeGallery() {
        this.destroyGallery();
        if (typeof subSliderInit === "function") subSliderInit(true, 8, this.modalContent);
        if (typeof sliderInit === "function") sliderInit(true, this.modalContent);
      }

      onResize() {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          if (!this.hasAttribute("open") || !this.modalContent?.children.length) return;
          this.modalContent
            .querySelectorAll(".js-media-list, .js-media-sublist")
            .forEach((element) => element.swiper?.update());
        }, 180);
      }
    }
  );
}
